import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Signup() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referrer, setReferrer] = useState(null);
  // After register the backend now returns {ok, email_verification_required}
  // and does NOT issue a token. We hold this object so the page swaps to a
  // "Check your inbox" state with a Resend button.
  const [pendingVerify, setPendingVerify] = useState(null); // { email } | null
  const [resending, setResending] = useState(false);

  // Pick up ?ref=CODE, persist across auth redirects, and validate.
  useEffect(() => {
    const raw = (params.get("ref") || localStorage.getItem("tapin_pending_ref") || "").trim().toUpperCase();
    if (!raw) return;
    setReferralCode(raw);
    try { localStorage.setItem("tapin_pending_ref", raw); } catch {}
    (async () => {
      try {
        const { data } = await api.post("/referrals/validate", { code: raw });
        if (data?.valid) setReferrer(data.referrer);
      } catch {}
    })();
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password, referral_code: referralCode || undefined });
      try { localStorage.removeItem("tapin_pending_ref"); } catch {}
      // Backend no longer returns a token at signup — user must confirm
      // their email first. Switch to the "check your inbox" state.
      if (data?.email_verification_required) {
        setPendingVerify({ email: data.email || email });
        toast.success("Confirmation email sent. Check your inbox to finish.");
        return;
      }
      // Fallback path (e.g., a legacy backend that still returns a token)
      // — keep the old auto-login flow so we don't break older deployments.
      if (data?.token) {
        loginWithToken(data.token, data.user);
        toast.success(referrer ? `Account created — ${referrer.name} is tracking your invite` : "Account created");
        nav("/");
      }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Signup failed");
    } finally { setBusy(false); }
  };

  const resendVerification = async () => {
    if (!pendingVerify?.email || resending) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email: pendingVerify.email });
      if (data?.rate_limited) {
        toast("We just sent one — check spam, or try again in 60 seconds.", { duration: 4500 });
      } else {
        toast.success("Confirmation email sent. Check your inbox.");
      }
    } catch (_e) {
      toast.error("We couldn't resend right now. Try again in a minute.");
    } finally { setResending(false); }
  };

  return (
    <div data-testid="signup-page" className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10 relative">
      <img src="https://customer-assets.emergentagent.com/job_live-broadcast-53/artifacts/xnmramec_0f112e07-9ce5-403f-a155-6e62b27aadfc.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/75 to-black" />

      <div className="relative tv-glass rounded-2xl p-8 w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-6">
          <img src="/tapintv-logo-v2.png" alt="Tap IN TV" className="h-56 w-auto drop-shadow-[0_0_50px_rgba(250,204,21,0.45)]" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold mt-4">
            {pendingVerify ? "Almost there" : "Join the network"}
          </div>
          <div className="font-display font-black text-3xl text-white mt-1">
            {pendingVerify ? "Check your inbox" : "Create account"}
          </div>
          {referrer && !pendingVerify && (
            <div data-testid="signup-referral-banner" className="mt-3 inline-flex items-center gap-2 bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-100 text-xs px-3 py-1.5 rounded-full">
              <span>🎁</span>
              Invited by <strong className="text-white">{referrer.name}</strong>
            </div>
          )}
        </div>

        {pendingVerify ? (
          <div data-testid="signup-pending-verify" className="space-y-4">
            <div className="text-center text-sm text-white/80 leading-relaxed">
              We sent a confirmation link to<br/>
              <b className="text-white" data-testid="signup-pending-email">{pendingVerify.email}</b>.<br/>
              Click it to unlock your account.
            </div>
            <button
              type="button"
              data-testid="signup-resend-verification-btn"
              onClick={resendVerification}
              disabled={resending}
              className="w-full bg-tv-accent hover:bg-yellow-400 disabled:opacity-60 text-black font-bold py-3 rounded-full transition"
            >
              {resending ? "Resending…" : "Resend confirmation email"}
            </button>
            <div className="text-xs text-white/40 text-center">
              Wrong email?{" "}
              <button
                type="button"
                onClick={() => setPendingVerify(null)}
                className="text-tv-brand hover:underline"
              >
                Use a different one
              </button>
            </div>
            <div className="mt-4 text-sm text-white/70 text-center">
              Already confirmed? <Link to="/login" className="text-tv-brand hover:underline font-semibold">Sign in</Link>
            </div>
          </div>
        ) : (<>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider font-semibold">Name</label>
            <input data-testid="signup-name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand" placeholder="Alex Rivera" />
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider font-semibold">Email</label>
            <input data-testid="signup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand" placeholder="you@email.com" />
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider font-semibold">Password (min 6)</label>
            <input data-testid="signup-password" type="password" minLength={6} required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-brand" />
          </div>
          {err && <div className="text-sm text-red-400" data-testid="signup-error">{err}</div>}
          <button data-testid="signup-submit" disabled={busy} className="w-full bg-tv-accent hover:bg-yellow-400 text-black font-bold py-3 rounded-full transition disabled:opacity-60">
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>

        <div className="relative my-5 text-center">
          <span className="bg-transparent text-white/40 text-xs uppercase tracking-wider">or</span>
        </div>

        <button
          data-testid="signup-google-btn"
          onClick={() => {
            const redirectUrl = window.location.origin + "/auth/callback";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full border border-white/20 hover:bg-white/5 text-white font-semibold py-3 rounded-full flex items-center justify-center gap-3"
        >
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#fff" d="M21.35 11.1h-9.17v2.8h5.28c-.23 1.4-1.7 4.1-5.28 4.1-3.18 0-5.78-2.63-5.78-5.87S8.99 6.26 12.18 6.26c1.81 0 3.02.77 3.72 1.43l2.54-2.44C16.8 3.6 14.74 2.75 12.18 2.75 6.98 2.75 2.8 6.93 2.8 12.13s4.18 9.38 9.38 9.38c5.41 0 8.99-3.8 8.99-9.16 0-.62-.07-1.08-.16-1.25z"/></svg>
          Continue with Google
        </button>

        <div className="mt-6 text-sm text-white/70 text-center">
          Already a member? <Link to="/login" className="text-tv-brand hover:underline font-semibold">Sign in</Link>
        </div>
        </>)}
      </div>
    </div>
  );
}
